import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardCheck, Plus, Trash2, MessageSquareQuote, AlertTriangle,
  CheckCircle2, Clock3, CircleDot, X, ChevronDown, ChevronRight, Calendar, User2,
} from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import { toast } from 'sonner';

const STATUS_META = {
  open: { label: 'Open', color: 'bg-slate-100 text-slate-700 border-slate-300', dot: 'text-slate-500', icon: CircleDot },
  in_progress: { label: 'In Progress', color: 'bg-amber-50 text-amber-700 border-amber-300', dot: 'text-amber-500', icon: Clock3 },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700 border-emerald-300', dot: 'text-emerald-500', icon: CheckCircle2 },
};
const PRIORITY_META = {
  low: 'bg-sky-50 text-sky-700 border-sky-200',
  medium: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  high: 'bg-rose-50 text-rose-700 border-rose-200',
};

const isOverdue = (a) => a.status !== 'completed' && a.due_date && new Date(a.due_date) < new Date();
const fmtDate = (d) => d ? new Date(d).toLocaleString([], { year: '2-digit', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export const CorrectiveActionsPanel = ({ incident }) => {
  const incidentId = incident?.id;
  const [observations, setObservations] = useState([]);
  const [actions, setActions] = useState([]);
  const [comments, setComments] = useState([]);
  const [owners, setOwners] = useState({ users: [], departments: [], crews: [] });
  const [showObsForm, setShowObsForm] = useState(false);
  const [showActForm, setShowActForm] = useState(false);
  const [addActionFor, setAddActionFor] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    if (!incidentId) return;
    try {
      const [obs, acts, cmts] = await Promise.all([
        CommanServices.listObservations(incidentId),
        CommanServices.listIncidentCorrectiveActions(incidentId),
        CommanServices.listIncidentComments(incidentId),
      ]);
      if (obs?.status) setObservations(obs.data || []);
      if (acts?.status) setActions(acts.data || []);
      if (cmts?.status) setComments(cmts.data || []);
    } catch (e) { /* noop */ }
  }, [incidentId]);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener('corrective-actions-refresh', onRefresh);
    (async () => {
      try {
        const [u, d, t] = await Promise.all([
          CommanServices.getAppUsers(),
          CommanServices.departments(),
          CommanServices.getTeamsData(),
        ]);
        const users = (Array.isArray(u) ? u : (u?.data || u || [])).map(x => ({ id: x.id, name: x.name || x.full_name || x.email }));
        const departments = (Array.isArray(d) ? d : (d?.data || d || [])).map(x => ({ id: x.id, name: x.department_name || x.name }));
        const teamsArr = Array.isArray(t) ? t : (t?.data || t || []);
        const crews = [];
        teamsArr.forEach(tm => (tm.crews || []).forEach(c => crews.push({ id: c.id, name: `${tm.team_name} / ${c.crew_name}` })));
        setOwners({ users, departments, crews });
      } catch (e) { /* noop */ }
    })();
    return () => window.removeEventListener('corrective-actions-refresh', onRefresh);
  }, [load]);

  const ownerByValue = useMemo(() => {
    const map = {};
    owners.users.forEach(o => { map[`user:${o.id}`] = o.name; });
    owners.departments.forEach(o => { map[`department:${o.id}`] = o.name; });
    owners.crews.forEach(o => { map[`crew:${o.id}`] = o.name; });
    return map;
  }, [owners]);

  // Real incidents get the same continuous-improvement panel as drills — the
  // review just reads "Post-Incident Review" instead of "AAR".
  const isDrill = incident?.is_exercise === true;

  const overdueCount = actions.filter(isOverdue).length;
  const openCount = actions.filter(a => a.status !== 'completed').length;

  const onStatusChange = async (a, status) => {
    const res = await CommanServices.updateCorrectiveAction(a.id, { status });
    if (res?.status) { toast.success('Status updated'); load(); }
  };
  const onDeleteAction = async (a) => {
    const res = await CommanServices.deleteCorrectiveAction(a.id);
    if (res?.status) { toast.success('Action deleted'); load(); }
  };
  const onDeleteObs = async (o) => {
    const res = await CommanServices.deleteObservation(o.id);
    if (res?.status) { toast.success('Observation deleted'); load(); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="corrective-actions-panel">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <button
          type="button"
          data-testid="corrective-actions-collapse-toggle"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 text-left group"
        >
          <ClipboardCheck className="w-5 h-5 text-indigo-600" />
          <div>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1">
              Corrective Actions &amp; {isDrill ? 'AAR' : 'Review'}
              {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
            </h3>
            <p className="text-[11px] text-gray-500">Lessons learned and improvement tracking for this {isDrill ? 'exercise' : 'incident'}</p>
          </div>
        </button>
        <div className="flex items-center gap-2">
          {overdueCount > 0 && (
            <span className="flex items-center gap-1 text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 px-2 py-1 rounded-full" data-testid="ca-overdue-badge">
              <AlertTriangle className="w-3 h-3" /> {overdueCount} overdue
            </span>
          )}
          <span className="text-[11px] font-semibold text-gray-500">{openCount} open</span>
        </div>
      </div>

      {!collapsed && (<>
      {/* Observations */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
            <MessageSquareQuote className="w-3.5 h-3.5" /> Observations ({observations.length})
          </h4>
          <button onClick={() => setShowObsForm(v => !v)} data-testid="add-observation-btn"
            className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800">
            {showObsForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} {showObsForm ? 'Cancel' : 'Add'}
          </button>
        </div>
        {showObsForm && <ObservationForm incidentId={incidentId} comments={comments} onDone={() => { setShowObsForm(false); load(); }} />}
        <div className="space-y-2 mt-2">
          {observations.length === 0 && <p className="text-xs text-gray-400">No observations captured yet.</p>}
          {observations.map(o => {
            const obsActions = actions.filter(a => a.observation_id === o.id);
            return (
            <div key={o.id} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100" data-testid={`observation-${o.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{o.title}</p>
                  {o.note && <p className="text-xs text-gray-600 mt-0.5">{o.note}</p>}
                  <p className="text-[10px] text-gray-400 mt-1">
                    {o.source_type === 'comment' ? 'From discussion comment' : 'Manual'} · {o.created_by_name}
                  </p>
                  {obsActions.length > 0 && (
                    <div className="mt-1.5 space-y-1" data-testid={`obs-actions-${o.id}`}>
                      {obsActions.map(a => {
                        const sm = STATUS_META[a.status] || STATUS_META.open;
                        return (
                          <div key={a.id} className="flex items-center gap-1.5 text-[11px] text-gray-600 pl-2 border-l-2 border-indigo-200" data-testid={`obs-linked-action-${a.id}`}>
                            <ClipboardCheck className="w-3 h-3 text-indigo-500 shrink-0" />
                            <span className="truncate">{a.title}</span>
                            <span className={`ml-auto shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${sm.color}`}>{sm.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => setAddActionFor(addActionFor === o.id ? null : o.id)} data-testid={`add-action-for-obs-${o.id}`}
                    title="Add corrective action for this observation"
                    className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800">
                    {addActionFor === o.id ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} Action
                  </button>
                  <button onClick={() => onDeleteObs(o)} data-testid={`delete-observation-${o.id}`} className="text-gray-300 hover:text-rose-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {addActionFor === o.id && (
                <div className="mt-2.5">
                  <ActionForm incidentId={incidentId} observations={observations} owners={owners}
                    initialObservationId={o.id} lockObservation
                    onDone={() => { setAddActionFor(null); load(); }} />
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Corrective Actions */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
            <ClipboardCheck className="w-3.5 h-3.5" /> Corrective Actions ({actions.length})
          </h4>
          <button onClick={() => setShowActForm(v => !v)} data-testid="add-action-btn"
            className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800">
            {showActForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} {showActForm ? 'Cancel' : 'New action'}
          </button>
        </div>
        {showActForm && (
          <ActionForm incidentId={incidentId} observations={observations} owners={owners}
            onDone={() => { setShowActForm(false); load(); }} />
        )}
        <div className="space-y-2 mt-2">
          {actions.length === 0 && <p className="text-xs text-gray-400">No corrective actions yet.</p>}
          {actions.map(a => {
            const sm = STATUS_META[a.status] || STATUS_META.open;
            const obs = observations.find(o => o.id === a.observation_id);
            const od = isOverdue(a);
            const isExp = expanded[a.id];
            return (
              <div key={a.id} className={`rounded-lg border p-3 ${od ? 'border-rose-300 bg-rose-50/40' : 'border-gray-200 bg-white'}`} data-testid={`action-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${PRIORITY_META[a.priority] || PRIORITY_META.medium}`}>{(a.priority || 'medium').toUpperCase()}</span>
                      <p className="text-sm font-semibold text-gray-900">{a.title}</p>
                    </div>
                    {a.description && <p className="text-xs text-gray-600 mt-1">{a.description}</p>}
                    {obs && <p className="text-[11px] text-indigo-600 mt-1 flex items-center gap-1"><MessageSquareQuote className="w-3 h-3" /> {obs.title}</p>}
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1"><User2 className="w-3 h-3" /> {a.owner_name || '—'}</span>
                      <span className={`flex items-center gap-1 ${od ? 'text-rose-600 font-bold' : ''}`}>
                        <Calendar className="w-3 h-3" /> {fmtDate(a.due_date)} {od && '(overdue)'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <select value={a.status} onChange={(e) => onStatusChange(a, e.target.value)} data-testid={`action-status-${a.id}`}
                      className={`text-[10px] font-bold border rounded-md px-1.5 py-1 cursor-pointer ${sm.color}`}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                    <button onClick={() => onDeleteAction(a)} data-testid={`delete-action-${a.id}`} className="text-gray-300 hover:text-rose-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {(a.updates || []).length > 0 && (
                  <button onClick={() => setExpanded(p => ({ ...p, [a.id]: !p[a.id] }))}
                    className="mt-2 flex items-center gap-1 text-[10px] font-bold text-gray-400 hover:text-gray-600">
                    {isExp ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Activity ({a.updates.length})
                  </button>
                )}
                {isExp && (
                  <div className="mt-1.5 space-y-1 border-l-2 border-gray-200 pl-2">
                    {(a.updates || []).slice().reverse().map((u, i) => (
                      <div key={i} className="text-[11px] text-gray-500">
                        <span className="text-gray-400">{fmtDate(u.at)}</span> · <b>{u.by_name}</b>: {u.text}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </>)}
    </div>
  );
};

const ObservationForm = ({ incidentId, comments, onDone }) => {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [fromComment, setFromComment] = useState('');
  const [busy, setBusy] = useState(false);

  const onPickComment = (cid) => {
    setFromComment(cid);
    const c = comments.find(x => x.id === cid);
    if (c) { setTitle(c.message?.slice(0, 80) || 'Discussion observation'); setNote(c.message || ''); }
  };

  const submit = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(), note: note.trim(),
        source_type: fromComment ? 'comment' : 'manual',
        source_comment_id: fromComment || null,
      };
      const res = await CommanServices.createObservation(incidentId, payload);
      if (res?.status) { toast.success('Observation added'); onDone(); }
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-indigo-50/40 border border-indigo-100 rounded-lg p-3 space-y-2" data-testid="observation-form">
      {comments.length > 0 && (
        <select value={fromComment} onChange={(e) => onPickComment(e.target.value)} data-testid="observation-from-comment"
          className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5">
          <option value="">— Capture manually —</option>
          {comments.map(c => <option key={c.id} value={c.id}>{c.user_name}: {(c.message || '').slice(0, 60)}</option>)}
        </select>
      )}
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Observation title" data-testid="observation-title-input"
        className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" />
      <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Details / lesson learned" rows={2} data-testid="observation-note-input"
        className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5" />
      <button onClick={submit} disabled={busy} data-testid="save-observation-btn"
        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2 rounded-md disabled:opacity-50">
        Save observation
      </button>
    </div>
  );
};

const ActionForm = ({ incidentId, observations, owners, onDone, initialObservationId = '', lockObservation = false }) => {
  const [form, setForm] = useState({ title: '', description: '', observation_id: initialObservationId, owner: '', priority: 'medium', due_date: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.owner) { toast.error('Owner required'); return; }
    if (!form.due_date) { toast.error('Due date required'); return; }
    const [owner_type, owner_id] = form.owner.split(':');
    const ownerName = (() => {
      const lists = { user: owners.users, department: owners.departments, crew: owners.crews };
      return (lists[owner_type] || []).find(o => o.id === owner_id)?.name || '';
    })();
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(), description: form.description.trim(),
        observation_id: form.observation_id || null,
        owner_type, owner_id, owner_name: ownerName,
        priority: form.priority, status: 'open',
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      };
      const res = await CommanServices.createCorrectiveAction(incidentId, payload);
      if (res?.status) { toast.success('Corrective action created'); onDone(); }
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-indigo-50/40 border border-indigo-100 rounded-lg p-3 space-y-2" data-testid="action-form">
      <input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Action title" data-testid="action-title-input"
        className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" />
      <textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Description" rows={2} data-testid="action-desc-input"
        className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5" />
      {lockObservation ? (
        <p className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600">
          <MessageSquareQuote className="w-3 h-3" /> {observations.find(o => o.id === initialObservationId)?.title || 'Linked observation'}
        </p>
      ) : (
        <select value={form.observation_id} onChange={(e) => set('observation_id', e.target.value)} data-testid="action-observation-select"
          className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5">
          <option value="">Link to observation (optional)</option>
          {observations.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
        </select>
      )}
      <div className="grid grid-cols-2 gap-2">
        <select value={form.owner} onChange={(e) => set('owner', e.target.value)} data-testid="action-owner-select"
          className="text-xs border border-gray-200 rounded-md px-2 py-1.5">
          <option value="">Responsible owner</option>
          <optgroup label="Users">{owners.users.map(o => <option key={o.id} value={`user:${o.id}`}>{o.name}</option>)}</optgroup>
          <optgroup label="Departments">{owners.departments.map(o => <option key={o.id} value={`department:${o.id}`}>{o.name}</option>)}</optgroup>
          <optgroup label="Crews">{owners.crews.map(o => <option key={o.id} value={`crew:${o.id}`}>{o.name}</option>)}</optgroup>
        </select>
        <select value={form.priority} onChange={(e) => set('priority', e.target.value)} data-testid="action-priority-select"
          className="text-xs border border-gray-200 rounded-md px-2 py-1.5">
          <option value="low">Low priority</option>
          <option value="medium">Medium priority</option>
          <option value="high">High priority</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase text-gray-400">Due date</label>
        <input type="datetime-local" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} data-testid="action-due-input"
          className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5" />
      </div>
      <button onClick={submit} disabled={busy} data-testid="save-action-btn"
        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2 rounded-md disabled:opacity-50">
        Create corrective action
      </button>
    </div>
  );
};

export default CorrectiveActionsPanel;
